import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<void>;
}

const images = [
  "/slider/image-1.jpg",
  "/slider/image-2.webp",
  "/slider/image-3.webp",
];

export default function LoginPage({ onLogin }: LoginPageProps) {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onLogin(username, password);
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ background: "#3d36fa", fontFamily: "Poppins, sans-serif" }}
    >
      <Paper
        elevation={8}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          width: 900,
          height: 520,
          display: "flex",
        }}
      >
        {/* Left: Form */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          px={8}
          py={6}
          bgcolor="#fff"
        >
          <Typography variant="h4" fontWeight={700} mb={0.5} color="text.primary">
            Login
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" mb={4}>
            Gemini game cafe
          </Typography>
          <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Username"
              variant="outlined"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              fullWidth
              InputProps={{
                sx: { borderRadius: 999, background: "#fff" },
              }}
            />
            <TextField
              label="Password"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              fullWidth
              InputProps={{
                sx: { borderRadius: 999, background: "#fff" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(v => !v)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <Typography color="error" variant="body2" mb={1}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                borderRadius: 999,
                bgcolor: "#3d36fa",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 18,
                py: 1.5,
                mt: 1,
                '&:hover': { bgcolor: theme.palette.primary.dark },
              }}
              disabled={loading}
            >
              {loading ? "Loading..." : "Login"}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" align="center" mt={6}>
            ©2025 Gemini Game Cafe. All Rights Reserved.
          </Typography>
        </Box>
        {/* Right: Image Slider */}
        <Box
          flex={1}
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ bgcolor: "#222", p: 0 }}
        >
          <Box
            component="img"
            src={images[currentImage]}
            alt="slider"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderTopRightRadius: 32,
              borderBottomRightRadius: 32,
            }}
          />
          {/* Slider dots */}
          <Box
            position="absolute"
            bottom={24}
            left="50%"
            sx={{ transform: "translateX(-50%)" }}
            display="flex"
            gap={2}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentImage(idx)}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid #fff",
                  background: idx === currentImage ? "#fff" : "rgba(255,255,255,0.5)",
                  boxShadow: idx === currentImage ? 3 : 0,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
} 